import { SERVICE_ACCESS_REQUIRED_ERROR_CODE } from "@repo/app-types/api/user";
import { z } from "zod";
import { buildAppUrl, hqBaseUrl } from "../../helpers/appUrls.js";
import {
  buildCodeChallenge,
  checkShouldRefreshServiceAccess,
  decideServiceAccessEntry,
  generateRandomBase64Url,
  parseServiceAccessReturnPath,
  resolveServiceAccessCallback,
  SERVICE_ACCESS_CALLBACK_PATH,
  SERVICE_ACCESS_FRAGMENT,
  type PendingServiceAccessSignIn,
  type ServiceAccessEntry,
} from "./serviceAccessFlow.js";
import { serviceAccessCompanyId } from "./serviceAccessMode.js";

/**
 * The admin client of service access: sign-in through HQ with PKCE, the tokens
 * in this origin's `localStorage` shared by its tabs, and the refresh policy.
 */

const CLIENT_ID = "alpharun-admin";

/** This origin's tokens, shared by all its tabs. */
const TOKENS_STORAGE_KEY = "alpharun.serviceAccess.tokens";
/** Set once a sign-in completes here, so a later visit without a token signs in. */
const USED_STORAGE_KEY = "alpharun.serviceAccess.used";
/** The tab's sign-in under way, in `sessionStorage`. */
const PENDING_SIGN_IN_STORAGE_KEY = "alpharun.serviceAccess.pendingSignIn";

/** Only one tab of the origin rotates the refresh token at a time. */
const REFRESH_LOCK_NAME = "alpharun.serviceAccess.refresh";

/** How often input may trigger a check for an early refresh. */
const INPUT_REFRESH_CHECK_INTERVAL_MS = 30 * 1000;

const INPUT_EVENT_TYPES = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
] as const;

const storedTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  accessExpiresAtMs: z.number(),
  /** Random per session, so a new session never reuses cached account data. */
  sessionKey: z.string(),
});

/** Tokens as the token endpoint issued them. */
interface IssuedServiceAccessTokens {
  accessToken: string;
  refreshToken: string;
  accessExpiresAtMs: number;
}

type StoredServiceAccessTokens = z.infer<typeof storedTokensSchema>;

const pendingSignInSchema = z.object({
  state: z.string(),
  codeVerifier: z.string(),
  returnPath: z.string(),
});

const tokenResponseSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  expires_in: z.number(),
});

const tokenErrorResponseSchema = z.object({ error: z.string() });

const serviceAccessRequiredBodySchema = z.object({
  code: z.literal(SERVICE_ACCESS_REQUIRED_ERROR_CODE),
});

let entry: ServiceAccessEntry | null = null;
let callbackParams: { code: string | null; state: string | null } | null = null;
/** `performance.now()` of the last real user input. */
let lastInputAt = Number.NEGATIVE_INFINITY;
let lastInputRefreshCheckAt = Number.NEGATIVE_INFINITY;
let refreshInFlight: Promise<StoredServiceAccessTokens | null> | null = null;
/** Set once the tab finds its session over; a new sign-in reloads the page. */
let hasEnded = false;
const endListeners = new Set<() => void>();

/**
 * Decides what this page load does in service mode, and starts it. Its
 * synchronous part runs before Sentry starts, so the callback's code is off
 * the URL before anything can record it. The app renders once it resolves.
 */
export async function startServiceAccess(): Promise<void> {
  if (!serviceAccessCompanyId) {
    return;
  }

  const url = new URL(window.location.href);
  const isCallback = url.pathname === SERVICE_ACCESS_CALLBACK_PATH;
  if (isCallback) {
    // The default strict-origin-when-cross-origin already keeps the code from
    // other origins; no-referrer is defense in depth for anything this page loads.
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.append(meta);
    callbackParams = {
      code: url.searchParams.get("code"),
      state: url.searchParams.get("state"),
    };
    url.search = "";
  }
  const hasServiceFragment = url.hash === SERVICE_ACCESS_FRAGMENT;
  if (hasServiceFragment) {
    url.hash = "";
  }
  if (url.href !== window.location.href) {
    window.history.replaceState(window.history.state, "", url);
  }

  entry = decideServiceAccessEntry({
    isCallback,
    hasTokens: readTokens() !== null,
    hasUsedServiceAccess:
      readStorage("localStorage", USED_STORAGE_KEY) !== null,
    hasServiceFragment,
  });

  switch (entry) {
    case "callback":
      return;
    case "app":
      trackPresence();
      await refreshStoredTokensOnLoad();
      return;
    case "sign_in":
      void startServiceAccessSignIn();
      return;
    case "leave": {
      const { origin, pathname, search, hash } = window.location;
      // A path like `//host` would otherwise leave for that host.
      const path = parseServiceAccessReturnPath(
        `${pathname}${search}${hash}`,
        origin,
      );
      window.location.replace(buildAppUrl(path));
      return;
    }
  }
}

/**
 * Refreshes stored tokens that are due before the app's first request, so a
 * session that ended since this origin last used it, such as yesterday's,
 * signs in again through HQ instead of failing that request.
 */
async function refreshStoredTokensOnLoad(): Promise<void> {
  const tokens = readTokens();
  if (!tokens || !checkShouldRefresh(tokens)) {
    return;
  }
  const refreshed = await refreshTokens(tokens).catch(() => tokens);
  if (!refreshed) {
    entry = "sign_in";
    await startServiceAccessSignIn();
  }
}

/** What {@link startServiceAccess} decided for this page load. */
export function readServiceAccessEntry(): ServiceAccessEntry | null {
  return entry;
}

/** The current session's cache key, or null without tokens. */
export function readServiceAccessSessionKey(): string | null {
  return readTokens()?.sessionKey ?? null;
}

/** Whether this tab found its service access session over. */
export function checkHasServiceAccessEnded(): boolean {
  return hasEnded;
}

/** Calls `listener` when the tab finds its session over, for `useSyncExternalStore`. */
export function subscribeToServiceAccessEnd(listener: () => void): () => void {
  endListeners.add(listener);
  return () => {
    endListeners.delete(listener);
  };
}

/**
 * Sends the tab to HQ's authorize page, which comes back to the callback with
 * a code once the employee has touched their passkey.
 */
async function startServiceAccessSignIn(): Promise<void> {
  if (!serviceAccessCompanyId) {
    throw new Error("Service access sign-in outside service mode");
  }

  const codeVerifier = generateRandomBase64Url(32);
  const state = generateRandomBase64Url(32);
  const { pathname, search, hash } = window.location;
  const pending: PendingServiceAccessSignIn = {
    state,
    codeVerifier,
    returnPath:
      pathname === SERVICE_ACCESS_CALLBACK_PATH
        ? "/"
        : `${pathname}${search}${hash}`,
  };
  if (
    !writeStorage(
      "sessionStorage",
      PENDING_SIGN_IN_STORAGE_KEY,
      JSON.stringify(pending),
    )
  ) {
    throw new Error("Service access sign-in needs sessionStorage");
  }

  const codeChallenge = await buildCodeChallenge(codeVerifier);
  const authorizeUrl = new URL(
    `/companies/${serviceAccessCompanyId}/authorize-service-access`,
    hqBaseUrl,
  );
  authorizeUrl.searchParams.set("state", state);
  authorizeUrl.searchParams.set("code_challenge", codeChallenge);
  authorizeUrl.searchParams.set("redirect_uri", buildRedirectUri());
  authorizeUrl.searchParams.set("client_id", CLIENT_ID);
  window.location.assign(authorizeUrl.href);
}

/**
 * Exchanges the callback's code for this origin's tokens, and returns the
 * same-origin path to go back to. Throws when the callback did not come from
 * this tab's sign-in, or the code was refused.
 */
export async function completeServiceAccessSignIn(): Promise<string> {
  const pending = readPendingSignIn();
  removeStorage("sessionStorage", PENDING_SIGN_IN_STORAGE_KEY);
  const resolved = resolveServiceAccessCallback({
    pending,
    code: callbackParams?.code ?? null,
    state: callbackParams?.state ?? null,
  });
  callbackParams = null;
  if (!resolved) {
    throw new Error("Service access callback without a matching sign-in");
  }

  const tokens = await requestTokens({
    grant_type: "authorization_code",
    code: resolved.code,
    code_verifier: resolved.codeVerifier,
    redirect_uri: buildRedirectUri(),
    client_id: CLIENT_ID,
  });
  if (!tokens) {
    throw new Error("Service access code refused");
  }
  writeTokens({ ...tokens, sessionKey: generateRandomBase64Url(16) });
  writeStorage("localStorage", USED_STORAGE_KEY, "true");

  return parseServiceAccessReturnPath(
    resolved.returnPath,
    window.location.origin,
  );
}

/**
 * The access token for a request, refreshed first when it is about to expire
 * and a person is present. Otherwise a stale token goes out as it is, and its
 * 401 waits for the person to come back.
 */
export async function fetchServiceAccessToken(): Promise<string> {
  const tokens = readTokens();
  if (!tokens) {
    markServiceAccessEnded();
    throw new Error("Attempting to make a request with no service access");
  }
  if (!checkShouldRefresh(tokens)) {
    return tokens.accessToken;
  }
  const refreshed = await refreshTokens(tokens);
  if (!refreshed) {
    markServiceAccessEnded();
    throw new Error("Service access session is over");
  }
  return refreshed.accessToken;
}

/**
 * Continues after a click on a screen that needs a sign-in: a refresh when the
 * session still allows one, else a new sign-in through HQ.
 */
export async function resumeServiceAccess(): Promise<void> {
  const tokens = readTokens();
  if (tokens) {
    const refreshed = await refreshTokens(tokens).catch(() => null);
    if (refreshed) {
      // The callback has spent its code, so it goes to the app root instead.
      if (window.location.pathname === SERVICE_ACCESS_CALLBACK_PATH) {
        window.location.replace("/");
      } else {
        window.location.reload();
      }
      return;
    }
  }
  await startServiceAccessSignIn();
}

/**
 * Marks the session over when an API error response says the service access
 * token no longer resolves.
 */
export function handleServiceAccessRequiredResponse(
  httpStatus: unknown,
  body: unknown,
): void {
  if (
    httpStatus === 401 &&
    serviceAccessRequiredBodySchema.safeParse(body).success
  ) {
    markServiceAccessEnded();
  }
}

/**
 * Ends the session on the server, forgets its tokens and leaves for HQ. The
 * tokens go even if the server can't be reached; the session then lapses at
 * its idle timeout.
 */
export async function endServiceAccess(): Promise<void> {
  try {
    const accessToken = await fetchServiceAccessToken();
    await fetch(buildApiUrl("/service-access/end"), {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    // Leave regardless.
  }
  // Under the refresh lock, so a rotation in another tab can't store fresh
  // tokens after these are gone; a later one finds none and stops.
  await runWithRefreshLock(async () => {
    removeStorage("localStorage", TOKENS_STORAGE_KEY);
  });
  window.location.assign(hqBaseUrl);
}

/**
 * Records that the session is over. The person signs in again from the notice
 * this shows, rather than being sent away mid-task.
 */
function markServiceAccessEnded(): void {
  if (hasEnded) {
    return;
  }
  hasEnded = true;
  for (const listener of endListeners) {
    listener();
  }
}

/**
 * Watches for a person at this tab. `startServiceAccess` calls it once per
 * page load, from main.tsx, so its listeners are never removed: they last as
 * long as the page, and ending service access navigates away.
 */
function trackPresence(): void {
  // Loading the page or bringing it forward is a person's act; a tab opened
  // in the background waits for input.
  const markPresentIfVisible = () => {
    if (document.visibilityState === "visible") {
      lastInputAt = performance.now();
    }
  };
  markPresentIfVisible();
  // In the capture phase on window, so these run before RTK Query's own
  // listeners refetch on focus. A window focus also arrives when another app
  // had focus; an element's focus is no one's act.
  window.addEventListener("visibilitychange", markPresentIfVisible, {
    capture: true,
  });
  window.addEventListener(
    "focus",
    (event) => {
      if (event.isTrusted && event.target === window) {
        markPresentIfVisible();
      }
    },
    { capture: true },
  );
  const handleInput = (event: Event) => {
    if (!event.isTrusted) {
      return;
    }
    lastInputAt = performance.now();
    if (
      lastInputAt - lastInputRefreshCheckAt <
      INPUT_REFRESH_CHECK_INTERVAL_MS
    ) {
      return;
    }
    lastInputRefreshCheckAt = lastInputAt;
    // Refresh ahead of the next request while the person is working, so a
    // session stays alive through reading as well as clicking.
    const tokens = readTokens();
    if (tokens && checkShouldRefresh(tokens)) {
      void refreshTokens(tokens).then(
        (refreshed) => {
          if (!refreshed) {
            markServiceAccessEnded();
          }
        },
        () => {
          // A network failure; the next request tries again.
        },
      );
    }
  };
  for (const type of INPUT_EVENT_TYPES) {
    window.addEventListener(type, handleInput, {
      capture: true,
      passive: true,
    });
  }
}

function checkShouldRefresh(tokens: StoredServiceAccessTokens) {
  return checkShouldRefreshServiceAccess({
    nowMs: Date.now(),
    accessExpiresAtMs: tokens.accessExpiresAtMs,
    isVisible: document.visibilityState === "visible",
    msSinceInput: performance.now() - lastInputAt,
  });
}

/**
 * Rotates the refresh token once across this origin's tabs. Resolves to the
 * current tokens, or null when the session can't be refreshed any more and a
 * new sign-in is needed.
 */
function refreshTokens(
  presented: StoredServiceAccessTokens,
): Promise<StoredServiceAccessTokens | null> {
  refreshInFlight ??= runWithRefreshLock(() => rotateTokens(presented)).finally(
    () => {
      refreshInFlight = null;
    },
  );
  return refreshInFlight;
}

async function rotateTokens(
  presented: StoredServiceAccessTokens,
): Promise<StoredServiceAccessTokens | null> {
  // Another tab may have rotated while this one waited for the lock. Its
  // tokens are the ones to use: presenting the old refresh token again,
  // outside the server's grace, would end the session.
  const current = readTokens();
  if (!current || current.refreshToken !== presented.refreshToken) {
    return current;
  }
  const rotated = await requestTokens({
    grant_type: "refresh_token",
    refresh_token: current.refreshToken,
    client_id: CLIENT_ID,
  });
  if (!rotated) {
    removeStorage("localStorage", TOKENS_STORAGE_KEY);
    return null;
  }
  const next = { ...rotated, sessionKey: current.sessionKey };
  writeTokens(next);
  return next;
}

async function runWithRefreshLock<T>(callback: () => Promise<T>): Promise<T> {
  if (!("locks" in navigator)) {
    return callback();
  }
  const result: T = await navigator.locks.request(REFRESH_LOCK_NAME, callback);
  return result;
}

/**
 * A token request to api's OAuth token endpoint. Resolves to null when the
 * grant is refused, and throws on any other failure.
 */
async function requestTokens(
  params: Record<string, string>,
): Promise<IssuedServiceAccessTokens | null> {
  const response = await fetch(buildApiUrl("/service-access/token"), {
    method: "POST",
    body: new URLSearchParams(params),
  });
  const body: unknown = await response.json();
  if (response.ok) {
    const tokens = tokenResponseSchema.parse(body);
    return {
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      accessExpiresAtMs: Date.now() + tokens.expires_in * 1000,
    };
  }
  const error = tokenErrorResponseSchema.safeParse(body);
  if (response.status === 400 && error.data?.error === "invalid_grant") {
    return null;
  }
  throw new Error(`Service access token request failed (${response.status})`);
}

function buildApiUrl(path: string) {
  return new URL(path, import.meta.env.VITE_API_BASE_URL);
}

function buildRedirectUri() {
  return new URL(SERVICE_ACCESS_CALLBACK_PATH, window.location.origin).href;
}

function readTokens(): StoredServiceAccessTokens | null {
  return parseStoredJson(
    readStorage("localStorage", TOKENS_STORAGE_KEY),
    storedTokensSchema,
  );
}

function writeTokens(tokens: StoredServiceAccessTokens): void {
  writeStorage("localStorage", TOKENS_STORAGE_KEY, JSON.stringify(tokens));
}

function readPendingSignIn(): PendingServiceAccessSignIn | null {
  return parseStoredJson(
    readStorage("sessionStorage", PENDING_SIGN_IN_STORAGE_KEY),
    pendingSignInSchema,
  );
}

function parseStoredJson<T>(raw: string | null, schema: z.ZodType<T>) {
  if (raw === null) {
    return null;
  }
  try {
    const result = schema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

// Storage can throw, even on access, when the browser blocks it.

type StorageArea = "localStorage" | "sessionStorage";

function readStorage(area: StorageArea, key: string): string | null {
  try {
    return window[area].getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(area: StorageArea, key: string, value: string): boolean {
  try {
    window[area].setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeStorage(area: StorageArea, key: string): void {
  try {
    window[area].removeItem(key);
  } catch {
    // Nothing to do.
  }
}
